import { FC, useState } from "react";
import { AlertTriangle, Info, Plus, PhoneOff } from "lucide-react";
import { MAX_CONTACT_ATTEMPTS, NOT_REACHED_REMINDER_DAYS } from "@myapp/contract";
import { FormSection } from "../../../../shared/ui";
import DeleteItemDialog from "../../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { ConfirmStatusChangeDialog } from "../ConfirmStatusChangeDialog";
import type { ContactAttemptsController } from "../../model/useContactAttempts";
import { AttemptForm } from "./AttemptForm";
import { AttemptRow } from "./AttemptRow";
import { ContactCommentField } from "./ContactCommentField";

interface Props {
  controller: ContactAttemptsController;
}

const days = (n: number) => `${n} ${n === 1 ? "Tag" : "Tagen"}`;

/**
 * „Kontaktversuche" — the card under the title of a „Nicht erreicht" (or „Gelöscht") Auftrag: how
 * many of the three attempts are used, each attempt with date and time, and the one comment.
 *
 * Draws what the controller hands it and decides nothing. A „Gelöscht" Auftrag, or a viewer who
 * may not edit, gets the same card with the controls left out — the history stays readable.
 */
export const ContactAttemptsCard: FC<Props> = ({ controller }) => {
  const {
    status,
    attempts,
    summary,
    comment,
    canEdit,
    canAdd,
    canCancelOrder,
    add,
    isAdding,
    addError,
    askRemove,
    deleteDialog,
    saveComment,
    isSavingComment,
    commentError,
    cancelPrompt,
  } = controller;

  const [adding, setAdding] = useState(false);
  const isCancelled = status === "CANCELLED";
  // Rows may be removed in any status (a wrong entry on a „Gelöscht" Auftrag is still wrong), but
  // only by those who may edit at all.
  const onRemove = canEdit ? askRemove : undefined;
  const nextPosition = attempts.length + 1;

  const subline = isCancelled
    ? "Der Auftrag ist auf „Gelöscht“ gesetzt"
    : summary.count === 0
      ? "Noch kein Versuch erfasst"
      : summary.isFull
        ? `Alle ${MAX_CONTACT_ATTEMPTS} Versuche erfasst`
        : null;
  const episode =
    !isCancelled && summary.daysSince !== null
      ? `Seit ${days(summary.daysSince)} nicht erreicht · ${
          summary.remindInDays ? `Erinnerung in ${days(summary.remindInDays)}` : "Erinnerung fällig"
        }`
      : null;

  return (
    <FormSection title="Kontaktversuche" cardClassName="relative grid gap-6 p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] md:gap-8 md:p-6">
      {/* The red hairline marks the card as the status's own, without a coloured edge on one side. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[20px] border-[1.5px] border-[#D6454166] md:rounded-[30px]"
      />

      <div>
        <div className="flex items-start gap-3.5">
          <div
            aria-hidden="true"
            className="grid h-11 w-11 flex-none place-items-center rounded-[14px] bg-[#D645412A] text-[#D64541]"
          >
            <PhoneOff size={24} />
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="font-['PT_Sans'] text-[28px] font-bold leading-[1.05] tracking-tight text-[#D64541] md:text-[34px]" aria-live="polite">
              <span className="tabular-nums">{summary.count}</span>
              <small className="ml-1.5 text-lg font-normal text-[#A8322F]">von {MAX_CONTACT_ATTEMPTS} Versuchen</small>
            </div>
            <p className="text-sm text-slate-600">{[subline, episode].filter(Boolean).join(" · ")}</p>
          </div>
        </div>

        <ol aria-label="Versuche" className="mt-[18px] flex flex-col">
          {attempts.map((attempt, index) => (
            <AttemptRow key={attempt.id} attempt={attempt} position={index + 1} onRemove={onRemove} />
          ))}
          {canAdd && adding && (
            <AttemptForm
              position={nextPosition}
              isSaving={isAdding}
              error={addError}
              onSubmit={add}
              onCancel={() => setAdding(false)}
            />
          )}
          {canAdd && !adding && (
            <li className="grid min-h-[52px] grid-cols-[26px_minmax(0,1fr)_auto_32px] items-center gap-[10px] border-t border-slate-200 py-2.5 text-sm text-slate-600 first:border-t-0 first:pt-0 sm:grid-cols-[28px_minmax(0,1fr)_auto_32px] sm:gap-3 sm:text-[15px]">
              <span aria-hidden="true" className="grid h-[26px] w-[26px] place-items-center rounded-full border-[1.5px] border-dashed border-slate-300 text-[13px] sm:h-7 sm:w-7">
                {nextPosition}
              </span>
              <span>Noch offen</span>
              <span>—</span>
              <span />
            </li>
          )}
        </ol>

        {canCancelOrder ? (
          <div className="mt-[18px] flex gap-2.5 rounded-[14px] bg-[#D6454114] p-3 text-sm leading-[1.45] text-[#A8322F]">
            <AlertTriangle size={18} aria-hidden="true" className="mt-px flex-none" />
            <div className="flex flex-col items-start gap-2.5">
              <p>
                <b className="font-medium">3. Versuch erfasst.</b> Der Auftrag kann jetzt auf <b className="font-medium">„Gelöscht“</b> gesetzt werden.
              </p>
              <button
                type="button"
                onClick={cancelPrompt.open}
                className="h-[38px] rounded-full bg-[#A8322F] px-4 text-[15px] font-bold text-white transition-[filter] duration-150 hover:brightness-110"
              >
                Auf Gelöscht setzen
              </button>
            </div>
          </div>
        ) : (
          !isCancelled && (
            <div className="mt-[18px] flex gap-2.5 rounded-[14px] bg-[#F6F8FA] p-3 text-sm leading-[1.45] text-slate-700">
              <Info size={18} aria-hidden="true" className="mt-px flex-none text-slate-600" />
              <p>
                Nach dem <b className="font-medium text-slate-900">{MAX_CONTACT_ATTEMPTS}. Versuch</b> kann der Auftrag auf{" "}
                <b className="font-medium text-slate-900">„Gelöscht“</b> gesetzt werden. Nach{" "}
                <b className="font-medium text-slate-900">{NOT_REACHED_REMINDER_DAYS / 7} Wochen</b> in diesem Status geht eine Erinnerung an das Büro.
              </p>
            </div>
          )
        )}
      </div>

      <div className="flex flex-col gap-3.5">
        {canEdit ? (
          <ContactCommentField comment={comment} isSaving={isSavingComment} error={commentError} onSave={saveComment} />
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-slate-700">Kommentar</p>
            <p className="whitespace-pre-wrap text-base text-slate-800">{comment || "Kein Kommentar"}</p>
          </div>
        )}
        {canAdd && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={adding}
            className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-full border-[1.5px] border-[#D6454166] px-5 text-[17px] font-bold text-[#A8322F] transition-colors duration-150 hover:bg-[#D6454114] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={18} aria-hidden="true" />
            Weiteren Versuch erfassen
          </button>
        )}
      </div>

      <DeleteItemDialog deleteModal={deleteDialog} />
      <ConfirmStatusChangeDialog
        to={cancelPrompt.isOpen ? "CANCELLED" : null}
        from={status}
        isPending={cancelPrompt.isPending}
        error={cancelPrompt.error}
        onConfirm={cancelPrompt.confirm}
        onCancel={cancelPrompt.close}
      />
    </FormSection>
  );
};
